/**
 * System Configuration Service
 * 
 * API client for system configuration and prompt template management.
 */
import apiClient from './apiClient';

export interface GitHubConfigStatus {
  repository_url: string;
  token_configured: boolean;
  configured: boolean;
}

export interface GitHubConfigUpdate {
  repository_url: string;
  token?: string;
  clear_token: boolean;
}

export interface ConfigItem {
  key: string;
  value: string;
  category: string;
  description: string | null;
  value_type: string;
  sensitive: boolean;
  updated_at: string | null;
  updated_by: string | null;
  default?: string | null;
  min_value?: number | null;
  max_value?: number | null;
  min_length?: number | null;
  max_length?: number | null;
  choices?: string[] | null;
  // Grouped metadata
  group?: string;
  sub_group?: string;
  requires_restart?: boolean;
  display_order?: number;
}

export interface ConfigKeyMeta {
  key: string;
  description: string;
  value_type: string;
  sensitive: boolean;
  default: string;
  choices?: string[] | null;
  min_value?: number | null;
  max_value?: number | null;
  min_length?: number | null;
  max_length?: number | null;
  requires_restart: boolean;
  display_order: number;
  group: string;
  sub_group: string;
  category: string;
}

export interface ConfigSubGroup {
  id: string;
  label: string;
  keys: ConfigKeyMeta[];
}

export interface ConfigGroup {
  id: string;
  label: string;
  sub_groups: ConfigSubGroup[];
}

export interface ConfigValidationResult {
  healthy: boolean;
  total: number;
  valid: number;
  invalid: number;
  missing_count: number;
  missing_keys: string[];
  errors: { key: string; error: string }[];
  reload_status: string;
  cached_keys: number;
}

export interface PromptTemplate {
  prompt_name: string;
  prompt_text: string;
  provider: string;
  model: string;
  temperature: number;
  max_output_size: number;
  version: number;
  updated_at: string | null;
  updated_by: number | null;
}

export interface PromptVersion {
  version: number;
  prompt_text: string;
  provider: string;
  model: string;
  temperature: number;
  max_output_size: number;
  is_active: boolean;
  created_at: string | null;
  updated_by: number | null;
}

export interface LLMProviderSummary {
  id: string;
  display_name: string;
  is_active: boolean;
  model_count: number;
}

export interface LLMModelEntry {
  id: string;
  display_name: string;
}

export interface LLMProviderModels {
  provider: string;
  display_name: string;
  models: LLMModelEntry[];
}

export interface AuditLogEntry {
  id: number;
  config_key: string;
  old_value: string | null;
  new_value: string;
  changed_by: number;
  changed_at: string;
  change_type: string;
}

export interface ReloadResponse {
  success: boolean;
  configs_loaded: number;
  prompts_loaded: number;
  message: string;
}

class SystemConfigService {
  private baseUrl = '/api/v1/admin/system-config';

  async getGitHubConfig(): Promise<GitHubConfigStatus> {
    const response = await apiClient.get(`${this.baseUrl}/github`);
    return response.data;
  }

  async updateGitHubConfig(data: GitHubConfigUpdate): Promise<GitHubConfigStatus> {
    const response = await apiClient.put(`${this.baseUrl}/github`, data);
    return response.data;
  }

  /**
   * Get all configuration parameters
   */
  async getAllConfig(): Promise<ConfigItem[]> {
    const response = await apiClient.get(this.baseUrl);
    return response.data;
  }

  /**
   * Update a configuration parameter
   */
  async updateConfig(key: string, value: string): Promise<{ key: string; value: string; detail: string }> {
    const response = await apiClient.put(`${this.baseUrl}/${key}`, { value });
    return response.data;
  }

  /**
   * Reset a configuration parameter to its default value
   */
  async resetConfig(key: string): Promise<{ key: string; value: string; detail: string }> {
    const response = await apiClient.post(`${this.baseUrl}/${key}/reset`);
    return response.data;
  }

  /**
   * Reload all configuration from database
   */
  async reloadConfig(): Promise<{ detail: string }> {
    const response = await apiClient.post(`${this.baseUrl}/reload`);
    return response.data;
  }

  /**
   * Validate all configuration entries (health check)
   */
  async validateConfig(): Promise<ConfigValidationResult> {
    const response = await apiClient.get(`${this.baseUrl}/validate`);
    return response.data;
  }

  /**
   * Get all prompt templates
   */
  async getAllPrompts(): Promise<PromptTemplate[]> {
    const response = await apiClient.get(`${this.baseUrl}/prompts`);
    return response.data;
  }

  /**
   * Get a single prompt template
   */
  async getPrompt(promptName: string): Promise<PromptTemplate> {
    const response = await apiClient.get(`${this.baseUrl}/prompts/${promptName}`);
    return response.data;
  }

  /**
   * Update a prompt template (creates new version)
   */
  async updatePrompt(
    promptName: string,
    data: {
      prompt_text: string;
      model: string;
      temperature: number;
      max_output_size: number;
    }
  ): Promise<{ success: boolean; prompt_name: string; version: number; message: string }> {
    const response = await apiClient.put(`${this.baseUrl}/prompts/${promptName}`, data);
    return response.data;
  }

  /**
   * Get version history for a prompt
   */
  async getPromptVersions(promptName: string): Promise<PromptVersion[]> {
    const response = await apiClient.get(`${this.baseUrl}/prompts/${promptName}/versions`);
    return response.data;
  }

  /**
   * Delete a specific inactive prompt version
   */
  async deletePromptVersion(
    promptName: string,
    version: number
  ): Promise<{ deleted: boolean; prompt_name: string; version: number }> {
    const response = await apiClient.delete(`${this.baseUrl}/prompts/${promptName}/versions/${version}`);
    return response.data;
  }

  /**
   * Delete all inactive versions for a prompt
   */
  async cleanupInactivePromptVersions(
    promptName: string
  ): Promise<{ deleted_count: number; deleted_versions: number[] }> {
    const response = await apiClient.delete(`${this.baseUrl}/prompts/${promptName}/versions/cleanup`);
    return response.data;
  }

  /**
   * Delete all inactive versions across all prompts
   */
  async cleanupAllInactivePromptVersions(): Promise<{
    total_prompts_processed: number;
    total_versions_deleted: number;
    prompts_cleaned: Array<{
      prompt_name: string;
      deleted_count: number;
      deleted_versions: number[];
    }>;
  }> {
    const response = await apiClient.delete(`${this.baseUrl}/prompts/versions/cleanup-all`);
    return response.data;
  }

  /**
   * Get audit log entries
   */
  async getAuditLog(limit = 100, configKey?: string): Promise<AuditLogEntry[]> {
    const params = new URLSearchParams();
    params.append('limit', limit.toString());
    if (configKey) {
      params.append('config_key', configKey);
    }
    const response = await apiClient.get(`${this.baseUrl}/audit-log?${params.toString()}`);
    return response.data;
  }

  /**
   * Get list of configuration categories/sections
   */
  async getCategories(): Promise<string[]> {
    const response = await apiClient.get(`${this.baseUrl}/sections`);
    return response.data;
  }

  /**
   * Get grouped configuration metadata tree for UI rendering
   */
  async getMetadata(): Promise<ConfigGroup[]> {
    const response = await apiClient.get(`${this.baseUrl}/metadata`);
    return response.data;
  }

  /**
   * Get configuration entries for a specific section
   */
  async getBySection(section: string): Promise<ConfigItem[]> {
    const response = await apiClient.get(`${this.baseUrl}/section/${section}`);
    return response.data;
  }

  // ── LLM Provider / Model endpoints ──────────────────────────────────

  /**
   * Get all supported LLM providers
   */
  async getLLMProviders(): Promise<LLMProviderSummary[]> {
    const response = await apiClient.get('/api/v1/llm/providers');
    return response.data;
  }

  /**
   * Get available models for a specific LLM provider
   */
  async getLLMProviderModels(providerId: string): Promise<LLMProviderModels> {
    const response = await apiClient.get(`/api/v1/llm/providers/${providerId}/models`);
    return response.data;
  }
}

export default new SystemConfigService();
